import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Tooltip,
  InputBase,
  Paper,
  Popper,
  ClickAwayListener,
  Badge,
  Select,
  FormControl,
  Stack,
  Button,
} from '@mui/material';
import {
  Menu as MenuIcon,
  MenuOpen as MenuOpenIcon,
  Dashboard as DashboardIcon,
  Videocam as VideocamIcon,
  LiveTv as LiveTvIcon,
  Replay as ReplayIcon,
  People as PeopleIcon,
  Security as SecurityIcon,
  Logout as LogoutIcon,
  NotificationsActive as AlertsIcon,
  Notifications as NotificationsIcon,
  Map as MapIcon,
  Person as PersonIcon,
  VpnKey as LicenseIcon,
  Campaign as NotificationCenterIcon,
  SmartToy as AIModulesIcon,
  WarningAmber as AiEventsIcon,
  Search as SearchIcon,
  Business as OrgIcon,
  FiberManualRecord as DotIcon,
  HowToReg as HowToRegIcon,
  Badge as BadgeIcon,
  VolumeUp as SpeakerIcon,
  RecordVoiceOver as AnnouncementIcon,
  Psychology as IntelligenceIcon,
  VideoLibrary as VideoLibraryIcon,
  AutoAwesome as AutoAwesomeIcon,
  HealthAndSafety as SafetyIcon,
  School as SchoolIcon,
  Dns as DnsIcon,
  Face as FaceIcon,
} from '@mui/icons-material';
import { API_BASE } from '../config';
import { useNavigate, useLocation } from 'react-router-dom';
import ThemeSwitcher from '../components/ThemeSwitcher';
import GlobalLiveAnnouncementSpeaker from '../components/GlobalLiveAnnouncementSpeaker';
import { useUser } from '../components/UserProvider';
import { useLicense } from '../contexts/LicenseContext';
import { cameraService, eventService, aiModuleService } from '../services/api';
import NvidiaCopilotModal from '../components/NvidiaCopilotModal';

interface LayoutProps {
  children: React.ReactNode;
}

const drawerWidthExpanded = 220;
const drawerWidthCollapsed = 64;
const SIDEBAR_COLLAPSE_KEY = 'astravision_sidebar_collapsed';

type HealthLevel = 'success' | 'warning' | 'danger';

const HEALTH_COLOR: Record<HealthLevel, string> = {
  success: '#28C98B',
  warning: '#F5B942',
  danger: '#FF5263',
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const [profileAnchor, setProfileAnchor] = useState<null | HTMLElement>(null);
  const [searchAnchor, setSearchAnchor] = useState<null | HTMLElement>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [cameras, setCameras] = useState<{ id: number; name: string }[]>([]);
  const [unresolvedCount, setUnresolvedCount] = useState(0);
  const [health, setHealth] = useState<HealthLevel>('success');
  const [copilotOpen, setCopilotOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, hasPermission } = useUser();
  const { hasFeature, valid, loaded } = useLicense();

  const drawerWidth = collapsed ? drawerWidthCollapsed : drawerWidthExpanded;

  const handleLogout = () => {
    logout();
    localStorage.removeItem('astravision_token');
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    if (location.pathname === '/login' || location.pathname === '/register') return;

    const checkAuth = () => {
      const hasUserInStorage = localStorage.getItem('astravision_user');
      if (!hasUserInStorage) {
        localStorage.removeItem('astravision_user');
        setTimeout(() => navigate('/login', { replace: true }), 100);
        return;
      }
      if (!user && hasUserInStorage) return;
      if (user) return;
      navigate('/login', { replace: true });
    };

    checkAuth();
  }, [user, navigate, location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSE_KEY, next ? '1' : '0'); } catch { /* ignore */ }
      return next;
    });
  };

  const handleDrawerToggle = () => {
    if (isMobile) setMobileOpen(!mobileOpen);
    else toggleCollapsed();
  };

  // Global search corpus: nav items + cameras already loaded for this user.
  // No new backend search endpoint -- purely client-side over data the app
  // already fetches elsewhere.
  useEffect(() => {
    cameraService.getCameras().then((rows) => setCameras(rows.map((c: any) => ({ id: c.id, name: c.name })))).catch(() => {});
  }, []);

  // Unresolved-events badge + aggregate worker health dot -- both poll on
  // the same 30s cadence CameraMonitorPage already uses elsewhere in the app.
  const refreshAlerts = useCallback(() => {
    eventService.getEvents({ limit: 100 }).then((events) => {
      setUnresolvedCount(events.filter((e: any) => !e.is_acknowledged).length);
    }).catch(() => {});
    aiModuleService.listWorkerStatus().then((rows) => {
      if (!Array.isArray(rows) || rows.length === 0) { setHealth('success'); return; }
      const hasError = rows.some((r: any) => r.state === 'error');
      const hasDegraded = rows.some((r: any) => r.degraded);
      setHealth(hasError ? 'danger' : hasDegraded ? 'warning' : 'success');
    }).catch(() => {});
  }, []);

  useEffect(() => {
    refreshAlerts();
    const interval = setInterval(refreshAlerts, 30000);
    return () => clearInterval(interval);
  }, [refreshAlerts]);

  const mainNavItems = [
    { text: 'Dashboard',      icon: <DashboardIcon />, path: '/',               feature: null,             perm: 'dashboard:view',     section: 'Monitoring' },
    { text: 'Cameras',        icon: <VideocamIcon />,  path: '/cameras',        feature: 'cameras',        perm: 'cameras:view',       section: 'Monitoring' },
    { text: 'Live View',      icon: <LiveTvIcon />,    path: '/live-view',      feature: 'live_view',      perm: 'live_view:view',     section: 'Monitoring' },
    { text: 'Playback',       icon: <ReplayIcon />,    path: '/playback',       feature: 'playback',       perm: 'playback:view',      section: 'Monitoring' },
    { text: 'Local Recordings', icon: <VideoLibraryIcon />, path: '/local-recordings', feature: null,            perm: 'cameras:view',       section: 'Monitoring' },
    { text: 'Camera Monitor', icon: <AlertsIcon />,    path: '/camera-monitor', feature: 'camera_monitor', perm: 'camera_monitor:view', section: 'Monitoring' },
    { text: 'GIS & Floor Map',icon: <MapIcon />,       path: '/gis',            feature: 'gis_floor_map',  perm: 'gis:view',           section: 'Monitoring' },
    { text: 'Safety & Security', icon: <SafetyIcon />, path: '/safety-security', feature: 'analytics', perm: 'ai_modules:view', section: 'AI & Models' },
    { text: 'Campus Intelligence', icon: <SchoolIcon />, path: '/campus-intelligence', feature: 'analytics', perm: 'ai_modules:view', section: 'AI & Models' },
    { text: 'AI Modules',     icon: <AIModulesIcon />, path: '/ai-modules',     feature: 'analytics',      perm: 'ai_modules:view',    section: 'AI & Models' },
    { text: 'NVIDIA AI Studio', icon: <AutoAwesomeIcon sx={{ color: '#76B900' }} />, path: '/nvidia-ai', feature: null, perm: 'dashboard:view', section: 'AI & Models' },
    { text: 'AI Intelligence Center', icon: <AutoAwesomeIcon />, path: '/ai-intelligence-center', feature: 'analytics', perm: 'ai_modules:view', section: 'AI & Models' },
    { text: 'AI Events',      icon: <AiEventsIcon />,  path: '/ai-events',      feature: 'analytics',      perm: 'ai_events:view',     section: 'AI & Models' },
    { text: 'Person Intelligence', icon: <IntelligenceIcon />, path: '/activity-intelligence', feature: 'face_recognition', perm: 'ai_modules:view', section: 'AI & Models' },
    { text: 'Cross-Camera Search', icon: <SearchIcon sx={{ color: '#06B6D4' }} />, path: '/search', feature: 'cameras', perm: 'cameras:view', section: 'AI & Models' },
    { text: 'Known Faces & VIPs', icon: <FaceIcon sx={{ color: '#A855F7' }} />, path: '/known-faces', feature: 'face_recognition', perm: 'ai_modules:view', section: 'AI & Models' },
    { text: 'Attendance',     icon: <HowToRegIcon />,  path: '/attendance',     feature: 'face_recognition', perm: 'attendance:view',  section: 'Operations' },
    { text: 'Personnel Roster', icon: <BadgeIcon />,   path: '/students',       feature: 'face_recognition', perm: 'students:view',    section: 'Operations' },
    { text: 'IP Speakers',    icon: <SpeakerIcon />,   path: '/speakers',       feature: 'ip_speakers',    perm: 'speakers:view',      section: 'Operations' },
    { text: 'AI Announcements', icon: <AnnouncementIcon />, path: '/announcements', feature: 'voice_announcements', perm: 'announcements:view', section: 'Operations' },
    { text: 'Server & Storage Monitor', icon: <DnsIcon sx={{ color: '#3B82F6' }} />, path: '/server-management', feature: null, perm: 'dashboard:view', section: 'Operations' },
  ].filter(item => {
    // When license is not valid/activated, only Dashboard is permitted
    if (!valid && item.path !== '/') return false;
    // Feature gating strictly checked against active license
    if (item.feature && !hasFeature(item.feature)) return false;
    // Role/Permission gating
    return hasPermission(item.perm) || (item.path === '/ai-events' && hasPermission('ai_modules:view')) || (item.path === '/ai-intelligence-center' && hasPermission('ai_modules:view')) || (item.path === '/safety-security' && hasPermission('ai_modules:view')) || (item.path === '/campus-intelligence' && hasPermission('ai_modules:view')) || (user?.role === 'admin');
  });

  const bottomNavItems = [
    ...(valid ? [
      { text: 'Notifications', icon: <NotificationCenterIcon />,  path: '/notification-center', perm: 'notifications:view', feature: 'notifications' },
    ] : []),
    ...(user?.role === 'admin' ? [
      { text: 'Users',         icon: <PeopleIcon />,              path: '/users',               perm: 'users:view' },
      { text: 'Roles',         icon: <SecurityIcon />,            path: '/roles',               perm: 'roles:view' },
      { text: 'License',       icon: <LicenseIcon />,             path: '/license',             perm: '*' },
    ] : [
      { text: 'Users',         icon: <PeopleIcon />,              path: '/users',               perm: 'users:view' },
      { text: 'Roles',         icon: <SecurityIcon />,            path: '/roles',               perm: 'roles:view' },
    ]),
  ].filter(item => {
    if ('feature' in item && item.feature && !hasFeature(item.feature as string)) return false;
    return hasPermission(item.perm) || (user?.role === 'admin');
  });

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return { navs: [], cams: [] };
    const navs = mainNavItems.filter((n) => n.text.toLowerCase().includes(q)).slice(0, 5);
    const cams = cameras.filter((c) => c.name.toLowerCase().includes(q)).slice(0, 5);
    return { navs, cams };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery, cameras]);

  const navItemStyle = {
    borderRadius: '8px',
    mx: 0.75,
    transition: 'all 150ms ease',
    '&.Mui-selected': {
      background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark || theme.palette.primary.main} 100%)`,
      color: theme.palette.primary.contrastText,
      boxShadow: `0 4px 14px ${theme.palette.primary.main}55`,
      '&:hover': { background: theme.palette.primary.main },
      '& .MuiListItemIcon-root': { color: theme.palette.primary.contrastText },
    },
  };

  const renderNavItem = (item: { text: string; icon: React.ReactNode; path: string }) => {
    const button = (
      <ListItemButton
        onClick={() => { navigate(item.path); if (isMobile) setMobileOpen(false); }}
        selected={location.pathname === item.path}
        sx={{ ...navItemStyle, py: 0.85, px: collapsed ? 1.25 : 1.5, justifyContent: collapsed ? 'center' : 'flex-start' }}
      >
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32, '& svg': { fontSize: 20 }, justifyContent: 'center' }}>
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
          <ListItemText
            primary={item.text}
            slotProps={{ primary: { sx: { fontSize: '0.8125rem', fontWeight: location.pathname === item.path ? 600 : 500 } } }}
          />
        )}
      </ListItemButton>
    );
    return (
      <ListItem key={item.text} disablePadding sx={{ mb: 0.25 }}>
        {collapsed ? <Tooltip title={item.text} placement="right">{button}</Tooltip> : button}
      </ListItem>
    );
  };

  const sectionLabel = (label: string) => !collapsed && (
    <Typography
      variant="caption"
      sx={{ px: 2, pt: 1.5, pb: 0.5, display: 'block', color: 'text.disabled', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.6875rem', fontWeight: 700 }}
    >
      {label}
    </Typography>
  );

  const sections = Array.from(new Set(mainNavItems.map((i) => i.section)));

  const drawer = (
    <Box sx={{ width: drawerWidth, display: 'flex', flexDirection: 'column', height: '100%', bgcolor: 'background.paper', transition: 'width 200ms ease' }}>
      <Box sx={{ px: collapsed ? 1 : 1.75, py: 1.25, borderBottom: 1, borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: collapsed ? 'center' : 'flex-start' }}>
        <img src="/logo.png" alt="ASTRAWIN" style={{ width: collapsed ? 28 : 110, height: 'auto' }} />
      </Box>

      <List sx={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', pt: 0.5, pb: 0 }}>
        {sections.map((section) => (
          <React.Fragment key={section}>
            {sectionLabel(section as string)}
            {mainNavItems.filter((i) => i.section === section).map(renderNavItem)}
          </React.Fragment>
        ))}
      </List>

      <Divider />

      <List sx={{ pt: 0.5, pb: 0.5 }}>
        {bottomNavItems.map(renderNavItem)}
        <ListItem disablePadding sx={{ mb: 0.25 }}>
          {(() => {
            const btn = (
              <ListItemButton
                onClick={() => { handleLogout(); if (isMobile) setMobileOpen(false); }}
                sx={{ ...navItemStyle, py: 0.85, px: collapsed ? 1.25 : 1.5, justifyContent: collapsed ? 'center' : 'flex-start' }}
              >
                <ListItemIcon sx={{ minWidth: collapsed ? 0 : 32, '& svg': { fontSize: 20 }, justifyContent: 'center' }}>
                  <LogoutIcon />
                </ListItemIcon>
                {!collapsed && <ListItemText primary="Logout" slotProps={{ primary: { sx: { fontSize: '0.8125rem' } } }} />}
              </ListItemButton>
            );
            return collapsed ? <Tooltip title="Logout" placement="right">{btn}</Tooltip> : btn;
          })()}
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'background.paper',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
          backgroundImage: 'none',
          transition: theme.transitions.create(['width', 'margin', 'background-color'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar sx={{ gap: 1.5 }}>
          <IconButton color="inherit" aria-label="toggle sidebar" edge="start" onClick={handleDrawerToggle} size="small">
            {collapsed ? <MenuIcon fontSize="small" /> : <MenuOpenIcon fontSize="small" />}
          </IconButton>

          {/* Site/organization selector -- cosmetic single-org placeholder;
              no multi-tenant data model exists in this app today. */}
          <FormControl size="small" sx={{ display: { xs: 'none', sm: 'block' }, minWidth: 160 }}>
            <Select
              value="default"
              displayEmpty
              disabled
              startAdornment={<OrgIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />}
              sx={{
                fontSize: '0.8125rem', height: 34,
                '& .MuiSelect-select': { display: 'flex', alignItems: 'center', py: 0.5 },
                bgcolor: 'sentinel.panelElevated',
              }}
            >
              <MenuItem value="default">AstraVision HQ</MenuItem>
            </Select>
          </FormControl>

          {/* Global search */}
          <ClickAwayListener onClickAway={() => setSearchAnchor(null)}>
            <Box sx={{ flexGrow: 1, maxWidth: 420 }}>
              <Paper
                variant="outlined"
                sx={{
                  display: 'flex', alignItems: 'center', px: 1.25, height: 36,
                  bgcolor: 'sentinel.panelElevated', borderColor: 'divider',
                  borderRadius: '8px',
                }}
              >
                <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 1 }} />
                <InputBase
                  placeholder="Search cameras, features, categories, or use cases…"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setSearchAnchor(e.currentTarget.parentElement); }}
                  onFocus={(e) => setSearchAnchor(e.currentTarget.parentElement)}
                  sx={{ flex: 1, fontSize: '0.8125rem' }}
                />
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.2,
                    bgcolor: 'rgba(255, 255, 255, 0.06)',
                    borderRadius: '4px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    fontSize: '0.68rem',
                    fontFamily: 'monospace',
                    color: 'text.secondary',
                    fontWeight: 600,
                  }}
                >
                  Ctrl K
                </Box>
              </Paper>
              <Popper open={Boolean(searchAnchor) && searchQuery.trim().length > 0} anchorEl={searchAnchor} placement="bottom-start" style={{ zIndex: 1300, width: 340 }}>
                <Paper variant="outlined" sx={{ mt: 0.5, py: 0.5, maxHeight: 320, overflowY: 'auto', bgcolor: 'sentinel.panelElevated' }}>
                  {searchResults.navs.length === 0 && searchResults.cams.length === 0 && (
                    <Typography variant="caption" sx={{ px: 2, py: 1, display: 'block', color: 'text.secondary' }}>No matches</Typography>
                  )}
                  {searchResults.navs.length > 0 && (
                    <>
                      <Typography variant="caption" sx={{ px: 2, pt: 0.5, display: 'block', color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.625rem' }}>Pages</Typography>
                      {searchResults.navs.map((n) => (
                        <MenuItem key={n.path} onClick={() => { navigate(n.path); setSearchQuery(''); setSearchAnchor(null); }} sx={{ fontSize: '0.8125rem' }}>
                          {n.icon}<Box component="span" sx={{ ml: 1.5 }}>{n.text}</Box>
                        </MenuItem>
                      ))}
                    </>
                  )}
                  {searchResults.cams.length > 0 && (
                    <>
                      <Typography variant="caption" sx={{ px: 2, pt: 0.5, display: 'block', color: 'text.disabled', textTransform: 'uppercase', fontSize: '0.625rem' }}>Cameras</Typography>
                      {searchResults.cams.map((c) => (
                        <MenuItem key={c.id} onClick={() => { navigate('/cameras'); setSearchQuery(''); setSearchAnchor(null); }} sx={{ fontSize: '0.8125rem' }}>
                          <VideocamIcon fontSize="small" /><Box component="span" sx={{ ml: 1.5 }}>{c.name}</Box>
                        </MenuItem>
                      ))}
                    </>
                  )}
                </Paper>
              </Popper>
            </Box>
          </ClickAwayListener>

          <Box sx={{ flexGrow: 1 }} />

          {/* System health indicator */}
          <Tooltip title={`System health: ${health === 'success' ? 'Nominal' : health === 'warning' ? 'Degraded worker(s)' : 'Worker error(s) detected'}`}>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ px: 1, cursor: 'default' }}>
              <DotIcon sx={{ fontSize: 10, color: HEALTH_COLOR[health] }} />
              <Typography variant="caption" sx={{ display: { xs: 'none', sm: 'inline' }, color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>
                {health === 'success' ? 'Nominal' : health === 'warning' ? 'Degraded' : 'Error'}
              </Typography>
            </Stack>
          </Tooltip>

          {/* NVIDIA AI Co-Pilot Button */}
          <Tooltip title="NVIDIA AI Co-Pilot (Nemotron Incident Reasoning & AI Assistant)">
            <Button
              variant="outlined"
              size="small"
              onClick={() => setCopilotOpen(true)}
              startIcon={<AutoAwesomeIcon sx={{ color: '#76B900 !important', fontSize: 17 }} />}
              sx={{
                height: 30,
                px: 1.25,
                mx: 0.5,
                borderColor: 'rgba(118, 185, 0, 0.45)',
                color: theme.palette.mode === 'dark' ? '#A3E635' : '#3F6212',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(118, 185, 0, 0.12)' : 'rgba(118, 185, 0, 0.08)',
                fontWeight: 700,
                fontSize: '0.74rem',
                textTransform: 'none',
                borderRadius: '7px',
                display: { xs: 'none', sm: 'inline-flex' },
                '&:hover': {
                  borderColor: '#76B900',
                  bgcolor: 'rgba(118, 185, 0, 0.22)',
                },
              }}
            >
              NVIDIA AI
            </Button>
          </Tooltip>

          {/* Mobile NVIDIA icon */}
          <Tooltip title="NVIDIA AI Co-Pilot">
            <IconButton
              size="small"
              onClick={() => setCopilotOpen(true)}
              sx={{
                display: { xs: 'inline-flex', sm: 'none' },
                color: '#76B900',
                p: 0.75,
              }}
            >
              <AutoAwesomeIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Alert indicator */}
          <Tooltip title={`${unresolvedCount} unresolved event(s)`}>
            <IconButton color="inherit" size="small" onClick={() => navigate('/events')}>
              <Badge badgeContent={unresolvedCount} color="error" max={99}>
                <NotificationsIcon fontSize="small" />
              </Badge>
            </IconButton>
          </Tooltip>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GlobalLiveAnnouncementSpeaker />
            <ThemeSwitcher toolbar />
          </Box>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            onClick={e => setProfileAnchor(e.currentTarget)}
            sx={{ ml: 0.5, cursor: 'pointer', p: 0.5, borderRadius: '8px', '&:hover': { bgcolor: 'rgba(255,255,255,0.04)' } }}
          >
            <Avatar
              src={user?.profile_picture ? `${API_BASE}/uploads/${user.profile_picture}` : undefined}
              sx={{ width: 32, height: 32, bgcolor: '#84cc16', color: '#080B12', fontSize: 12, fontWeight: 800 }}
            >
              {(user?.full_name || user?.username || 'AU')
                .split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2)}
            </Avatar>
            <Box sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'left', lineHeight: 1.1 }}>
              <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 700, color: 'text.primary' }}>
                {user?.full_name || 'Admin User'}
              </Typography>
              <Typography variant="caption" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
                AstraVision
              </Typography>
            </Box>
          </Stack>

          <Menu
            anchorEl={profileAnchor}
            open={Boolean(profileAnchor)}
            onClose={() => setProfileAnchor(null)}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            slotProps={{ paper: { sx: { minWidth: 220, mt: 0.5 } } }}
          >
            <Box sx={{ px: 2, py: 1.25 }}>
              <Typography variant="body2" fontWeight={700} noWrap>{user?.full_name || user?.username}</Typography>
              <Typography variant="caption" color="text.secondary" noWrap>{user?.role}</Typography>
            </Box>
            <Divider />
            <MenuItem onClick={() => { navigate('/profile'); setProfileAnchor(null); }}>
              <PersonIcon fontSize="small" sx={{ mr: 1.5, opacity: 0.7 }} />
              My Profile
            </MenuItem>
            <Box sx={{ display: { xs: 'block', md: 'none' }, px: 2, py: 1.25, borderTop: 1, borderColor: 'divider' }}>
              <ThemeSwitcher compact />
            </Box>
            <Divider />
            <MenuItem onClick={() => { handleLogout(); setProfileAnchor(null); }} sx={{ color: 'error.main' }}>
              <LogoutIcon fontSize="small" sx={{ mr: 1.5 }} />
              Sign Out
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 }, transition: 'width 200ms ease' }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidthExpanded,
              bgcolor: 'background.paper',
              color: 'text.primary',
              borderRight: 1,
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="persistent"
          anchor="left"
          open={!isMobile}
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
              color: 'text.primary',
              transition: 'width 200ms ease',
              overflowX: 'hidden',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          minWidth: 0,
          bgcolor: 'background.default',
          minHeight: '100vh',
          color: 'text.primary',
          width: { md: `calc(100% - ${drawerWidth}px)` },
          transition: theme.transitions.create(['width', 'background-color'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.standard,
          }),
        }}
      >
        <Toolbar />
        {!valid && loaded && location.pathname !== '/license' && (
          <Box
            sx={{
              mb: 2,
              p: 1.5,
              borderRadius: 1.5,
              bgcolor: (t) => t.palette.mode === 'dark' ? 'rgba(239, 68, 68, 0.12)' : 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 1.5,
            }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center">
              <SecurityIcon sx={{ color: '#ef4444', fontSize: 24, flexShrink: 0 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#ef4444', fontSize: '0.85rem' }}>
                  AstraVision License Not Activated
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.75rem' }}>
                  System is running without an active license. Operational menus and live camera streams are restricted until activated.
                </Typography>
              </Box>
            </Stack>
            {user?.role === 'admin' && (
              <Button
                variant="contained"
                size="small"
                onClick={() => navigate('/license')}
                sx={{
                  bgcolor: '#ef4444',
                  color: '#ffffff',
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  px: 2,
                  py: 0.5,
                  '&:hover': { bgcolor: '#dc2626' },
                }}
              >
                Activate License Now
              </Button>
            )}
          </Box>
        )}
        {children}
      </Box>

      {/* Global NVIDIA AI Co-Pilot Modal */}
      <NvidiaCopilotModal open={copilotOpen} onClose={() => setCopilotOpen(false)} />
    </Box>
  );
};

export default Layout;
